/**
 * The mark a pe3d field wears when the language being edited overrides it:
 * the language code, and a click that puts the field back on "en"'s value.
 *
 * Panels that draw fields far from the editor (the config form) read it from
 * `Pe3dOverrideContext`: { language, isOverridden(kind, key), resetOverride(kind, key) },
 * null while "en" is edited or the release has no languages.
 */

import React, { createContext, useContext } from "react";
import { Tooltip } from "@material-ui/core";

export const Pe3dOverrideContext = createContext(null);

const MARK_COLOR = "#e0a85a";

export function LanguageMark({ language, onReset, disabled }) {
  return (
    <Tooltip title={disabled ? `Set for ${language}` : `Set for ${language} — click to use en's value`} arrow>
      <span
        role="button"
        onClick={(event) => {
          event.stopPropagation();
          if (!disabled) onReset();
        }}
        style={{
          marginLeft: 6,
          padding: "0 4px",
          borderRadius: 3,
          border: `1px solid ${MARK_COLOR}`,
          color: MARK_COLOR,
          fontSize: 10,
          lineHeight: "14px",
          textTransform: "uppercase",
          cursor: disabled ? "default" : "pointer",
          verticalAlign: "middle",
        }}
      >
        {language}
      </span>
    </Tooltip>
  );
}

/** The mark for one field, when the context says the field is overridden; else nothing. */
export function OverrideMark({ kind, fieldKey, disabled }) {
  const context = useContext(Pe3dOverrideContext);
  if (!context || !context.isOverridden(kind, fieldKey)) return null;
  return <LanguageMark language={context.language} disabled={disabled} onReset={() => context.resetOverride(kind, fieldKey)} />;
}

/** The context value for an editor (usePe3dEditor), or null on "en". */
export function overrideContextOf(editor) {
  if (!editor || !editor.localizationSupported || editor.language === "en") return null;
  return { language: editor.language, isOverridden: editor.isOverridden, resetOverride: editor.resetOverride };
}
